/**
 * openContext — the single-open invariant manager.
 *
 * The editor is always pointed at exactly one of:
 *   • a release (read-only, immutable artifact), OR
 *   • a branch (editable working copy).
 *
 * Switching is racy: the underlying file swap goes through
 * `tilePacket.updateFiles` which is async, and Redux subscribers + file
 * listeners can re-fire mid-swap. We protect with a generation counter —
 * the same pattern we use for `pendingNavSource` in EditorViewV2.tsx.
 *
 * Stage 3 of the GitHub workspace rollout. See:
 *   /Users/manasluthra/.claude/plans/noble-roaming-rainbow.md
 */

import { setBaseSaveId } from '@/features/editor/session/utils/save-base';
import type { OpenContext, OpenKind } from '@/shared/types/releases.types';

export type { OpenContext, OpenKind } from '@/shared/types/releases.types';

/**
 * Information about a detected drift between cloud and git for a branch.
 * Mirrors the backend `GET /api/apps/:appId/branches/:branch/drift` payload,
 * with `branch` added on the FE side after the request.
 */
export interface DriftInfo {
  branch: string;
  hasDrift: boolean;
  reason: 'git_advanced' | 'git_branch_gone' | 'no_cloud_saves' | 'in_sync';
  cloudBaseSha: string | null;
  currentGitSha: string | null;
  unpushedSaveCount: number;
  latestSaveAt: string | null;
}

/** Choice the user makes in the BranchDriftModal. */
export type DriftChoice =
  | { outcome: 'pull' }
  | { outcome: 'keep' }
  | { outcome: 'fork'; newBranchName: string }
  | { outcome: 'cancel' };

export interface OpenContextHostApi {
  /** Returns the current TilePacket app id. */
  getAppId(): string;
  /**
   * Replaces the working file map. Resolves once the swap is committed.
   * Should call `tilePacket.updateFiles` with a full replacement delta.
   */
  replaceFiles(files: Record<string, string | null>): Promise<void>;
  /** True if there are unsaved changes in the editor (branch mode). */
  hasUnsavedChanges(): boolean;
  /**
   * Show a confirm dialog when switching away from a dirty branch.
   * Resolves true if the user wants to discard. Default impl uses `confirm()`.
   */
  confirmDiscard(message: string): Promise<boolean>;
  /**
   * Show the BranchDriftModal and resolve when the user picks an option.
   * The host wires this to a React modal in EditorViewV2. The default
   * implementation falls back to a window.confirm so headless contexts
   * (tests, SSR) don't deadlock.
   */
  presentDriftChoice?(info: DriftInfo): Promise<DriftChoice>;
}

interface SwapState {
  kind: OpenKind;
  id: string;
  generation: number;
  abortController: AbortController;
}

export interface SwapStatus {
  swapping: boolean;
  /** Target of the in-flight swap. null when not swapping. */
  target: { kind: OpenKind; id: string } | null;
  /** Bumped whenever a swap starts or ends so subscribers can re-render. */
  generation: number;
}

type Listener = (ctx: OpenContext | null) => void;
type SwapListener = (status: SwapStatus) => void;

const listeners = new Set<Listener>();
const swapListeners = new Set<SwapListener>();
let current: OpenContext | null = null;
let pending: SwapState | null = null;
let generationCounter = 0;
let host: OpenContextHostApi | null = null;

function snapshotSwap(): SwapStatus {
  return {
    swapping: pending !== null,
    target: pending ? { kind: pending.kind, id: pending.id } : null,
    generation: generationCounter,
  };
}

function emitSwap(): void {
  const snap = snapshotSwap();
  for (const l of swapListeners) {
    try {
      l(snap);
    } catch {
      /* never break peers */
    }
  }
}

const defaultHost: OpenContextHostApi = {
  getAppId: () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('appId') || 'default';
  },
  replaceFiles: async () => {
    /* no-op until installed */
  },
  hasUnsavedChanges: () => false,
  confirmDiscard: async (msg: string) => {
    return typeof window !== 'undefined' && window.confirm(msg);
  },
};

/** Editor shell installs the host adapter on mount. */
export function installOpenContextHost(api: Partial<OpenContextHostApi>): void {
  host = { ...defaultHost, ...api };
}

function getHost(): OpenContextHostApi {
  return host || defaultHost;
}

function emit(): void {
  for (const l of listeners) {
    try {
      l(current);
    } catch {
      /* listeners can't break each other */
    }
  }
}

export function subscribeOpenContext(listener: Listener): () => void {
  listeners.add(listener);
  // Fire once with current state so subscribers don't have to call a getter.
  try {
    listener(current);
  } catch {
    /* swallow */
  }
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Subscribe to swap-state changes (swapping start, swapping end). Used by
 * UI affordances (progress bar, chip pulse) to animate during the file
 * fetch + tilePacket.updateFiles handoff.
 */
export function subscribeSwap(listener: SwapListener): () => void {
  swapListeners.add(listener);
  try {
    listener(snapshotSwap());
  } catch {
    /* swallow */
  }
  return () => {
    swapListeners.delete(listener);
  };
}

export function getOpenContext(): OpenContext | null {
  return current;
}

/* ── Fetch helpers (Stage 3 default impls — Stage 9 swaps repo metadata) ──── */

/**
 * Download every file in a save through the standard save endpoints:
 *   GET /api/apps/:appId/saves/:saveId          → manifest
 *   GET /api/apps/:appId/saves/:saveId/file?path → one file's contents
 *
 * Single download path used by everything that needs a save's files —
 * materialize, openCloud, "Keep cloud" drift, release reads. The caller
 * decides whether to discard the result based on a stale-payload guard.
 */
async function fetchSaveFiles(
  appId: string,
  saveId: string,
  signal: AbortSignal,
): Promise<{
  files: Record<string, string | null>;
  branch: string | null;
  baseGitSha: string | null;
  fileCount: number;
} | null> {
  const manifestRes = await fetch(
    `/api/apps/${encodeURIComponent(appId)}/saves/${encodeURIComponent(saveId)}`,
    { credentials: 'include', signal },
  );
  if (!manifestRes.ok) return null;
  const json = (await manifestRes.json()) as {
    manifest: {
      branch?: string | null;
      baseGitSha?: string | null;
      saveId: string;
      files?: { path: string; hash?: string; type?: string }[] | Record<string, unknown>;
    };
  };
  const fileEntries: { path: string; hash?: string; type?: string }[] = Array.isArray(
    json.manifest.files,
  )
    ? (json.manifest.files as { path: string; hash?: string; type?: string }[])
    : Object.keys(json.manifest.files || {}).map((path) => ({ path }));

  // CODE → fetch by immutable content-hash URL (`/~asset/cas/<hash>`) so the
  // browser disk cache serves unchanged files for free on reopen/reload. The v1
  // per-save `/file?path=` fetch (per-save + no-cache) is removed for CODE — we
  // have no pre-CAS apps. ASSET files (or anything without a CAS hash) still use
  // the path endpoint, which materializes their bytes.
  const HASH64 = /^[0-9a-f]{64}$/;

  const files: Record<string, string | null> = {};
  await Promise.all(
    fileEntries.map(async ({ path, hash, type }) => {
      if (type !== 'ASSET' && hash && HASH64.test(hash)) {
        const cr = await fetch(`/api/apps/${encodeURIComponent(appId)}/~asset/cas/${hash}`, {
          credentials: 'include',
          signal,
        });
        if (cr.ok) files[path] = await cr.text();
        return;
      }
      const r = await fetch(
        `/api/apps/${encodeURIComponent(appId)}/saves/${encodeURIComponent(
          saveId,
        )}/file?path=${encodeURIComponent(path)}`,
        { credentials: 'include', signal },
      );
      if (r.ok) files[path] = await r.text();
    }),
  );

  return {
    files,
    branch: json.manifest?.branch ?? null,
    baseGitSha: json.manifest?.baseGitSha ?? null,
    fileCount: fileEntries.length,
  };
}

/**
 * Materialize a branch as a cloud save and resolve to its files + the
 * git SHA the save is anchored at.
 *
 * Read-through cache semantics (backend-owned):
 *   - If a cloud save already exists for this branch at the current git
 *     HEAD, the existing save id is returned.
 *   - If not, the backend fetches from git, creates the save, returns
 *     the new save's id.
 *
 * Either way the editor opens from cloud (S3 + DB), never from a
 * transient GitHub fetch. The materialize endpoint only returns the
 * save id — file contents come down the same path as any save, via
 * fetchSaveFiles below.
 */
async function materializeBranchFiles(
  appId: string,
  branch: string,
  signal: AbortSignal,
): Promise<{
  files: Record<string, string | null>;
  headSha: string;
  saveId: string;
  cached: boolean;
}> {
  const res = await fetch(
    `/api/apps/${encodeURIComponent(appId)}/branches/${encodeURIComponent(branch)}/materialize`,
    { method: 'POST', credentials: 'include', signal },
  );
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Branch materialize failed (${res.status}): ${body}`);
  }
  const json = (await res.json()) as {
    baseGitSha: string;
    saveId: string;
    cached: boolean;
  };
  const downloaded = await fetchSaveFiles(appId, json.saveId, signal);
  return {
    files: downloaded?.files ?? {},
    headSha: json.baseGitSha,
    saveId: json.saveId,
    cached: json.cached,
  };
}

/**
 * Fetch the latest save row for a branch (or the cloud-only chain when
 * branch is null) and resolve to its file map. Used by openCloud and
 * the "Keep cloud changes" path of the drift resolver.
 */
async function fetchLatestSaveFiles(
  appId: string,
  branch: string | null,
  signal: AbortSignal,
): Promise<{
  files: Record<string, string | null>;
  saveId: string;
  baseGitSha: string | null;
} | null> {
  // Ref-first: if a branch ref exists (git-style movable pointer), open the save
  // it points at — so a rollback / checkpoint-restore changes what the editor
  // opens. Falls through to the newest-walk below when there's no ref (404, e.g.
  // SAVE_REFS off) or the ref's save can't load. See SAVE-REFS-AND-GIT.md.
  try {
    const refName = branch && branch.trim() ? branch.trim() : 'main';
    const refRes = await fetch(
      `/api/apps/${encodeURIComponent(appId)}/refs/${encodeURIComponent(refName)}`,
      { credentials: 'include', signal },
    );
    if (refRes.ok) {
      const { saveId } = (await refRes.json()) as { saveId?: string };
      if (saveId) {
        const dl = await fetchSaveFiles(appId, saveId, signal);
        if (
          dl &&
          dl.branch === branch &&
          !(dl.fileCount > 0 && Object.keys(dl.files).length === 0)
        ) {
          return { files: dl.files, saveId, baseGitSha: dl.baseGitSha };
        }
      }
    }
  } catch {
    /* ref resolution failed — fall through to the newest-walk */
  }

  const listRes = await fetch(`/api/apps/${encodeURIComponent(appId)}/saves`, {
    credentials: 'include',
    signal,
  });
  if (!listRes.ok) return null;
  const listJson = (await listRes.json()) as { saves: string[] | { id: string }[] };
  const ids: string[] = (listJson.saves || []).map((s: any) => (typeof s === 'string' ? s : s.id));
  if (ids.length === 0) return null;

  // Walk newest → oldest looking for a save that (a) matches the branch
  // filter AND (b) has real file contents in S3. Saves predating the
  // 2026-05-21 S3 migration have manifests but no S3 objects — opening
  // one of those would give us an empty editor. Skip and keep walking.
  // TODO: add `?branch=` to GET /api/apps/:appId/saves for efficiency.
  for (const id of ids) {
    const downloaded = await fetchSaveFiles(appId, id, signal);
    if (!downloaded) continue;
    if (downloaded.branch !== branch) continue;
    // Stale-payload guard.
    if (downloaded.fileCount > 0 && Object.keys(downloaded.files).length === 0) continue;
    return {
      files: downloaded.files,
      saveId: id,
      baseGitSha: downloaded.baseGitSha,
    };
  }
  return null;
}

async function fetchReleaseFiles(
  appId: string,
  releaseId: string,
  signal: AbortSignal,
): Promise<{
  files: Record<string, string | null>;
  saveId: string;
  version: string;
  branch: string;
  targetEnvId: string;
}> {
  const releaseRes = await fetch(
    `/api/apps/${encodeURIComponent(appId)}/releases/${encodeURIComponent(releaseId)}`,
    { credentials: 'include', signal },
  );
  if (!releaseRes.ok) {
    const body = await releaseRes.text();
    throw new Error(`Release fetch failed (${releaseRes.status}): ${body}`);
  }
  const releaseJson = (await releaseRes.json()) as { release: any };
  const release = releaseJson.release;
  // Accept either new `saveId` or legacy `commitSha` field on the release.
  const saveId: string = release.saveId || release.commitSha;

  const manifestRes = await fetch(
    `/api/apps/${encodeURIComponent(appId)}/saves/${encodeURIComponent(saveId)}`,
    { credentials: 'include', signal },
  );
  if (!manifestRes.ok) {
    const body = await manifestRes.text();
    throw new Error(`Save manifest fetch failed (${manifestRes.status}): ${body}`);
  }
  const manifestJson = (await manifestRes.json()) as {
    manifest: { files: Record<string, unknown> };
  };
  const fileEntries = manifestJson.manifest?.files || {};

  // The manifest only lists metadata; fetch each file's contents.
  const paths = Object.keys(fileEntries);
  const files: Record<string, string | null> = {};
  await Promise.all(
    paths.map(async (path) => {
      const r = await fetch(
        `/api/apps/${encodeURIComponent(appId)}/saves/${encodeURIComponent(
          saveId,
        )}/file?path=${encodeURIComponent(path)}`,
        { credentials: 'include', signal },
      );
      if (r.ok) files[path] = await r.text();
    }),
  );

  return {
    files,
    saveId,
    version: release.version,
    branch: release.branch,
    targetEnvId: release.targetEnvId,
  };
}

/* ── Public API ──────────────────────────────────────────────────────────── */

export interface OpenBranchOpts {
  /** Override the dirty-confirm prompt. */
  confirmDiscardMessage?: string;
  /**
   * Skip the drift check. Used by the drift-resolution paths (pull/keep/
   * fork) to avoid re-prompting after the user has already chosen.
   */
  bypassDriftCheck?: boolean;
  /**
   * Where to load files from:
   *   'git'   (default) — fetch the branch's HEAD from GitHub
   *   'cloud' — load the latest cloud save row for this branch instead.
   *             Used by the "Keep cloud changes" path.
   */
  loadFrom?: 'git' | 'cloud';
}

/**
 * Open a GitHub branch in the editor.
 *
 * Flow:
 *   1. Dirty-check the current working copy; confirm discard if needed.
 *   2. Drift-check: ask the backend whether this branch's latest cloud
 *      save was based on the same git commit that's currently HEAD.
 *      If not, hand off to the host's drift-modal presenter and dispatch
 *      based on the user's choice (pull / keep / fork / cancel).
 *   3. Fetch files (from git or from the latest cloud save) and swap.
 *   4. Emit the new openContext.
 */
export async function openBranch(name: string, opts: OpenBranchOpts = {}): Promise<void> {
  const h = getHost();
  if (h.hasUnsavedChanges()) {
    const ok = await h.confirmDiscard(
      opts.confirmDiscardMessage ||
        `You have unsaved changes. Switch to branch "${name}" and discard them?`,
    );
    if (!ok) return;
  }

  // Drift check before any file swap, unless explicitly bypassed (drift
  // resolution paths use bypassDriftCheck so they don't re-prompt).
  if (!opts.bypassDriftCheck && h.presentDriftChoice) {
    try {
      const appId = h.getAppId();
      const driftRes = await fetch(
        `/api/apps/${encodeURIComponent(appId)}/branches/${encodeURIComponent(name)}/drift`,
        { credentials: 'include' },
      );
      if (driftRes.ok) {
        const drift = (await driftRes.json()) as DriftInfo;
        if (drift.hasDrift) {
          const choice = await h.presentDriftChoice({ ...drift, branch: name });
          switch (choice.outcome) {
            case 'cancel':
              return; // stay on current context
            case 'pull':
              // Discard cloud chain, load from git.
              return openBranch(name, { ...opts, bypassDriftCheck: true, loadFrom: 'git' });
            case 'keep':
              // Continue editing on top of cloud chain.
              return openBranch(name, { ...opts, bypassDriftCheck: true, loadFrom: 'cloud' });
            case 'fork': {
              // Backend creates the new branch on GitHub from the current
              // git HEAD, then we open it. The cloud chain stays on the
              // original branch and gets manually pushed/discarded later.
              const newBranch = choice.newBranchName.trim();
              if (!newBranch) return;
              const createRes = await fetch(
                `/api/apps/${encodeURIComponent(appId)}/github/branches`,
                {
                  method: 'POST',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ name: newBranch, fromBranch: name }),
                },
              );
              if (!createRes.ok) {
                // eslint-disable-next-line no-console
                console.error('[openContext] fork-create failed:', await createRes.text());
                return;
              }
              return openBranch(newBranch, { ...opts, bypassDriftCheck: true, loadFrom: 'git' });
            }
          }
        }
      }
    } catch (e) {
      // Drift check is best-effort — never block opening on its failure.
      // eslint-disable-next-line no-console
      console.warn('[openContext] drift check failed (continuing):', e);
    }
  }

  // Cancel any in-flight swap.
  if (pending) pending.abortController.abort();

  generationCounter += 1;
  const myGeneration = generationCounter;
  const ac = new AbortController();
  pending = { kind: 'branch', id: name, generation: myGeneration, abortController: ac };
  emitSwap();

  try {
    const appId = h.getAppId();
    let files: Record<string, string | null>;
    let basedOn: string | undefined;

    if (opts.loadFrom === 'cloud') {
      // "Keep cloud" path — load the cloud chain's latest save's files.
      // basedOn stays at the cloud chain's stored baseGitSha so a future
      // push correctly flags it as overwriting an outdated git HEAD.
      const cloud = await fetchLatestSaveFiles(appId, name, ac.signal);
      if (myGeneration !== generationCounter) return;
      if (!cloud) {
        // No cloud chain exists despite drift saying otherwise — fall back
        // to materializing from git HEAD.
        const mat = await materializeBranchFiles(appId, name, ac.signal);
        if (myGeneration !== generationCounter) return;
        files = mat.files;
        basedOn = mat.headSha;
        setBaseSaveId(null); // materialized (no save row) → no optimistic base
      } else {
        files = cloud.files;
        basedOn = cloud.baseGitSha ?? undefined;
        setBaseSaveId(cloud.saveId); // base future saves' 409-check on this tip
      }
    } else {
      // Default path — materialize. Backend either returns the cached
      // cloud copy for this branch+commit, or creates one fresh from
      // git. Either way the editor opens from our cloud.
      const mat = await materializeBranchFiles(appId, name, ac.signal);
      if (myGeneration !== generationCounter) return;
      files = mat.files;
      basedOn = mat.headSha;
      setBaseSaveId(null);
    }

    await h.replaceFiles(files);
    if (myGeneration !== generationCounter) return;

    current = {
      kind: 'branch',
      id: name,
      name,
      version: '',
      build: null,
      envId: null,
      envKind: 'dev',
      envName: '',
      branch: name,
      readOnly: false,
      dirty: false,
      basedOn,
    };
    pending = null;
    emit();
    emitSwap();
  } catch (e: any) {
    if (e?.name !== 'AbortError') {
      // eslint-disable-next-line no-console
      console.error('[openContext] openBranch failed:', e);
    }
    if (myGeneration === generationCounter) {
      pending = null;
      emitSwap();
    }
  }
}

/**
 * Open the cloud working copy — files come from the latest `app_saves`
 * row where `branch IS NULL`. Sets openContext kind='cloud'; future
 * saves will be persisted with `branch=null` and `baseGitSha=null`.
 */
export async function openCloud(): Promise<void> {
  const h = getHost();
  if (h.hasUnsavedChanges()) {
    const ok = await h.confirmDiscard(
      'You have unsaved changes. Switch to Cloud and discard them?',
    );
    if (!ok) return;
  }

  if (pending) pending.abortController.abort();

  generationCounter += 1;
  const myGeneration = generationCounter;
  const ac = new AbortController();
  pending = { kind: 'cloud', id: 'cloud', generation: myGeneration, abortController: ac };
  emitSwap();

  try {
    const appId = h.getAppId();
    const cloud = await fetchLatestSaveFiles(appId, null, ac.signal);
    if (myGeneration !== generationCounter) return;

    // Brand-new apps always have at least one cloud save from createApp(),
    // but stay defensive: if no cloud save exists, keep current files and
    // just flip the openContext so future saves get tagged correctly.
    if (cloud) {
      await h.replaceFiles(cloud.files);
      if (myGeneration !== generationCounter) return;
    }
    setBaseSaveId(cloud ? cloud.saveId : null);

    current = {
      kind: 'cloud',
      id: 'cloud',
      name: 'Cloud',
      version: '',
      build: null,
      envId: null,
      envKind: 'dev',
      envName: 'Cloud',
      branch: '',
      readOnly: false,
      dirty: false,
      basedOn: undefined,
    };
    pending = null;
    emit();
    emitSwap();
  } catch (e: any) {
    if (e?.name !== 'AbortError') {
      // eslint-disable-next-line no-console
      console.error('[openContext] openCloud failed:', e);
    }
    if (myGeneration === generationCounter) {
      pending = null;
      emitSwap();
    }
  }
}

export async function openRelease(releaseId: string): Promise<void> {
  const h = getHost();
  if (h.hasUnsavedChanges()) {
    const ok = await h.confirmDiscard(
      'You have unsaved changes. Open this release (read-only) and discard them?',
    );
    if (!ok) return;
  }

  if (pending) pending.abortController.abort();

  generationCounter += 1;
  const myGeneration = generationCounter;
  const ac = new AbortController();
  pending = { kind: 'release', id: releaseId, generation: myGeneration, abortController: ac };
  emitSwap();

  try {
    const appId = h.getAppId();
    const data = await fetchReleaseFiles(appId, releaseId, ac.signal);
    if (myGeneration !== generationCounter) return;

    await h.replaceFiles(data.files);
    if (myGeneration !== generationCounter) return;

    current = {
      kind: 'release',
      id: releaseId,
      name: data.version,
      version: data.version,
      build: null,
      envId: data.targetEnvId,
      envKind: 'prod',
      envName: '',
      branch: data.branch,
      readOnly: true,
      dirty: false,
    };
    setBaseSaveId(null); // releases are read-only — no saves, no optimistic base
    pending = null;
    emit();
    emitSwap();
  } catch (e: any) {
    if (e?.name !== 'AbortError') {
      // eslint-disable-next-line no-console
      console.error('[openContext] openRelease failed:', e);
    }
    if (myGeneration === generationCounter) {
      pending = null;
      emitSwap();
    }
  }
}

/**
 * Drop the current open context — used on logout/app teardown.
 */
export function clearOpenContext(): void {
  if (pending) pending.abortController.abort();
  pending = null;
  current = null;
  setBaseSaveId(null);
  generationCounter += 1;
  emit();
  emitSwap();
}

/**
 * Mark the current branch as dirty/clean. Called by the editor shell whenever
 * unsaved-edit state changes so subscribers (chip, banner) re-render.
 */
export function setDirty(dirty: boolean): void {
  if (!current || current.kind !== 'branch') return;
  if (current.dirty === dirty) return;
  current = { ...current, dirty };
  emit();
}

/** True iff a swap is in progress. UI can use this to show a spinner. */
export function isSwapping(): boolean {
  return pending !== null;
}
